<div class="page-comparison-container">
  <div class="header-section">
    <div>
      <h1>Page Comparison</h1>
      <p class="subtitle">Compare your page against a competitor to understand ranking differences</p>
    </div>
  </div>

  <div class="input-section">
    <div class="input-group">
      <label for="yourPageUrl">Your Page URL</label>
      <input 
        type="text" 
        id="yourPageUrl" 
        [(ngModel)]="yourPageUrl" 
        placeholder="https://example.com/your-page"
        [disabled]="loading"
      />
    </div>

    <div class="input-group">
      <label for="competitorPageUrl">Competitor Page URL</label>
      <input 
        type="text" 
        id="competitorPageUrl" 
        [(ngModel)]="competitorPageUrl" 
        placeholder="https://competitor.com/their-page"
        [disabled]="loading"
      />
    </div>

    <div class="button-group">
      <button 
        *ngIf="!isProduction && !featureFlags.isUserView()"
        class="autofill-button"
        (click)="autofill()"
        [disabled]="loading"
      >
        🧪 Autofill Test URLs
      </button>
      <button 
        class="compare-button" 
        (click)="runFullComparison()" 
        [disabled]="loading || !yourPageUrl || !competitorPageUrl || (comparisonProgress !== null && comparisonProgress.currentStep !== 'Completed!')"
      >
        {{ (comparisonProgress && comparisonProgress.currentStep !== 'Completed!') ? 'Running...' : (loading ? 'Comparing...' : 'Start Comparison') }}
      </button>
      <button 
        *ngIf="executionId && yourPage.status === 'completed' && competitorPage.status === 'completed' && !shareUrl"
        class="share-button"
        (click)="createShareLink()"
        [disabled]="shareLoading"
      >
        {{ shareLoading ? '⏳' : '🔗' }} Share
      </button>
    </div>

    <!-- Share link inline UI -->
    <div *ngIf="shareUrl" class="share-inline">
      <input class="share-url-input" type="text" [value]="shareUrl" readonly #shareInput (click)="shareInput.select()" />
      <button class="share-copy-btn" (click)="copyShareUrl()">
        {{ shareCopied ? '✓ Copied' : '📋 Copy' }}
      </button>
      <button class="share-dismiss-btn" (click)="dismissShare()" title="Dismiss">×</button>
    </div>
    <div *ngIf="shareError" class="share-error-msg">{{ shareError }}</div>
  </div>

  <!-- Progress Indicator (User Mode Only) -->
  <div class="comparison-progress" *ngIf="comparisonProgress">
    <div class="progress-header">
      <h3>Comparison Progress</h3>
      <span class="current-step">{{ comparisonProgress.currentStep }}</span>
    </div>
    <div class="progress-bar-container">
      <div class="progress-bar">
        <div class="progress-fill" [style.width.%]="progressPercentage"></div>
      </div>
      <span class="progress-percentage">{{ progressPercentage }}%</span>
    </div>
    <div class="progress-steps">
      <div 
        *ngFor="let step of progressSteps" 
        class="progress-step"
        [class.completed]="step.completed"
      >
        <span class="step-icon">{{ step.completed ? '✓' : '○' }}</span>
        <span class="step-name">{{ step.name }}</span>
      </div>
    </div>
    <div class="progress-time" *ngIf="comparisonProgress.estimatedTimeRemaining > 0">
      Estimated time remaining: {{ formatTime(comparisonProgress.estimatedTimeRemaining) }}
    </div>
  </div>

  <div class="comparison-results" *ngIf="yourPage.status !== 'idle' || competitorPage.status !== 'idle'">
    <div class="comparison-grid">
      <!-- Your Page Column -->
      <div class="page-column">
        <h2>Your Page</h2>
        
        <!-- Status Section -->
        <div class="status-section">
          <div class="status-indicator" [class]="'status-' + yourPage.status">
            <span class="status-dot"></span>
            <span class="status-text">{{ getStatusMessage(yourPage) }}</span>
          </div>

          <!-- Data Source Badge -->
          <div 
            *ngIf="yourPage.metadata.dataSource" 
            class="metadata-badge" 
            [class.from-database]="yourPage.metadata.dataSource === 'database'" 
            [class.from-api]="yourPage.metadata.dataSource === 'api'"
          >
            <span class="badge-icon">{{ yourPage.metadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ yourPage.metadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="yourPage.metadata.lastUpdated" class="badge-timestamp">
              ({{ yourPage.metadata.lastUpdated | date:'short' }})
            </span>
          </div>
        </div>

        <!-- Analysis Results -->
        <div class="analysis-results" *ngIf="hasAnalysis(yourPage)">
          <div class="metric-card">
            <div class="metric-label">Page Title</div>
            <div class="metric-value">{{ getPageTitle(yourPage) }}</div>
          </div>

          <div class="metric-card">
            <div class="metric-label">OnPage Score</div>
            <div class="metric-value score-value" [class]="'score-' + (getOnPageScore(yourPage) || 0 >= 70 ? 'good' : getOnPageScore(yourPage) || 0 >= 50 ? 'fair' : 'poor')">
              {{ getOnPageScore(yourPage) || 'N/A' }}
            </div>
          </div>

          <div class="metric-card">
            <div class="metric-label">Word Count</div>
            <div class="metric-value">{{ getWordCount(yourPage) || 'N/A' }}</div>
          </div>

          <!-- Freshness Card -->
          <div class="metric-card">
            <div class="metric-label">Freshness</div>
            <div class="freshness-content">
              <div class="freshness-row">
                <span class="freshness-label">Analyzed:</span>
                <span class="freshness-value">
                  <span *ngIf="getAnalyzedAt(yourPage); else noAnalyzedAt">
                    {{ getAnalyzedAt(yourPage) | date:'short' }}
                  </span>
                  <ng-template #noAnalyzedAt>N/A</ng-template>
                </span>
              </div>
              <div class="freshness-row" *ngIf="getLastModified(yourPage)">
                <span class="freshness-label">Last modified:</span>
                <span class="freshness-value">{{ getLastModified(yourPage) | date:'short' }}</span>
              </div>
            </div>
          </div>

          <!-- Topic Fit (Domain Topic Authority) -->
          <div class="metric-card topic-fit-card" *ngIf="getTopicSignalsCount(yourPage) > 0">
            <div class="metric-label">
              Topic Fit
              <span class="info-tooltip" title="Shows your domain's authority on this page's main topic. Requires domain crawl and topic aggregation.">ⓘ</span>
            </div>
            <ng-container *ngIf="getTopicFitData('yourPage') as topicFit">
              <!-- Loading state -->
              <div class="topic-fit-loading" *ngIf="topicFit.loading">
                <span class="loading-spinner-small"></span>
                Loading...
              </div>
              <!-- Error state -->
              <div class="topic-fit-error" *ngIf="topicFit.error">
                <span class="error-icon">⚠</span>
                Unable to load
              </div>
              <!-- No data state (domain not crawled/aggregated) -->
              <div class="topic-fit-unavailable" *ngIf="!topicFit.loading && !topicFit.error && !topicFit.hasData">
                <span class="muted-text">Domain topics not available yet</span>
                <span class="info-tooltip" title="Run a full domain crawl and topic aggregation to see this data.">ⓘ</span>
              </div>
              <!-- Topic not found in domain authority -->
              <div class="topic-fit-not-found" *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && !topicFit.topicFound">
                <span class="muted-text">Topic not in domain authority</span>
              </div>
              <!-- Topic found - show tier and score -->
              <div class="topic-fit-result" *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && topicFit.topicFound">
                <span class="tier-badge" [ngClass]="getTopicTierClass(topicFit.tier)">
                  {{ formatTopicTier(topicFit.tier) }}
                </span>
                <span class="topic-fit-details">
                  on <strong>{{ topicFit.mainTopicLabel }}</strong>
                  <span class="authority-score">(Authority {{ topicFit.authorityScore | number:'1.0-0' }})</span>
                </span>
              </div>
            </ng-container>
          </div>

          <!-- Topic Signals with Dropdown -->
          <div class="metric-card">
            <div class="metric-label">Topic Signals</div>
            <div class="metric-value">{{ getTopicSignalsCount(yourPage) }}</div>
            <div class="dropdown-section" *ngIf="getTopicSignalsCount(yourPage) > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleTopicSignals('yourPage')"
                type="button"
              >
                <span>{{ isTopicSignalsExpanded('yourPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isTopicSignalsExpanded('yourPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isTopicSignalsExpanded('yourPage')">
                <div class="topic-signals-grid">
                  <div 
                    class="topic-signal-card" 
                    [class.shared]="isTopicSignalShared(signal, 'yourPage')"
                    *ngFor="let signal of getTopicSignals(yourPage)"
                  >
                    <div class="topic-signal-label">{{ signal.label }}</div>
                    <div class="topic-signal-meta">
                      <span class="topic-signal-weight">{{ formatWeight(signal.weight) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Internal Links -->
          <div class="metric-card">
            <div class="metric-label">Internal Links</div>
            <div class="metric-value">{{ getLinksCount(yourPage) }}</div>
            <div class="link-warning" *ngIf="shouldShowLinkWarning(yourPageDomain, yourPage)">
              ⚠️ You can only classify links after a full domain crawl.
            </div>
            <div class="link-breakdown" *ngIf="shouldShowLinkBreakdown(yourPageDomain, yourPage)">
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('contextual')">Contextual</span>
                <strong>{{ getLinkStats(yourPage).contextual }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('structural')">Structural</span>
                <strong>{{ getLinkStats(yourPage).structural }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('same_page')">Same-page</span>
                <strong>{{ getLinkStats(yourPage).samePage }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('unknown')">Unknown</span>
                <strong>{{ getLinkStats(yourPage).unknown }}</strong>
              </div>
              <div class="link-note" *ngIf="getLinkClassificationStatus(yourPageDomain) === 'partial'">
                Partial classification for this domain.
              </div>
            </div>
            <div class="dropdown-section" *ngIf="getLinksCount(yourPage) > 0">
              <button
                class="dropdown-toggle"
                (click)="toggleInternalLinks('yourPage')"
                type="button"
              >
                <span>{{ isInternalLinksExpanded('yourPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isInternalLinksExpanded('yourPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isInternalLinksExpanded('yourPage')">
                <div class="link-context-note">
                  Full sentence context is not captured in the current link data.
                </div>
                <div class="link-list">
                  <div class="link-item" *ngFor="let link of getDisplayedInternalLinks(yourPage, 'yourPage')">
                    <div class="link-item-header">
                      <span class="link-type-pill" [ngClass]="getLinkTypeClass(link.linkType)" [title]="getLinkTypeTooltip(link.linkType)">
                        {{ getLinkTypeLabel(link.linkType) }}
                      </span>
                      <span class="link-meta" *ngIf="link.confidence != null">Confidence: {{ (link.confidence * 100) | number:'1.0-0' }}%</span>
                      <span class="link-meta" *ngIf="link.isBroken">Broken</span>
                      <span class="link-meta" *ngIf="link.dofollow === false">nofollow</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">From:</span>
                      <span class="link-item-value">{{ getLinkSourceUrl(yourPage) }}</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">To:</span>
                      <a class="link-item-value" [href]="link.targetUrl" target="_blank" rel="noopener noreferrer">
                        {{ link.targetUrl || link.targetRelativeUrl }}
                      </a>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Anchor:</span>
                      <span class="link-item-value">“{{ formatAnchorText(link.anchorText) }}”</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Context:</span>
                      <span class="link-item-value">Not available (link sentence not stored).</span>
                    </div>
                  </div>
                </div>
                <button
                  class="link-show-more"
                  *ngIf="hasMoreInternalLinks(yourPage, 'yourPage') || showAllInternalLinks.yourPage"
                  (click)="toggleShowAllInternalLinks('yourPage')"
                >
                  {{ showAllInternalLinks.yourPage ? 'Show less' : 'Show all links' }}
                </button>
                <div class="link-context-note" *ngIf="getFilteredInternalLinks(yourPage).length === 0">
                  No contextual or same-page links to show.
                </div>
              </div>
            </div>
          </div>

          <!-- Incoming Links -->
          <div class="metric-card" *ngIf="hasAnalysis(yourPage)">
            <div class="metric-label">Incoming Links</div>
            <div class="metric-value">{{ getIncomingLinksCount('yourPage') }}</div>
            <div class="link-warning" *ngIf="shouldShowIncomingLinksWarning('yourPage')">
              ⚠️ Full domain crawl recommended for complete incoming links data.
            </div>
            <div class="link-breakdown" *ngIf="getIncomingLinksCount('yourPage') > 0">
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('contextual')">Contextual</span>
                <strong>{{ getIncomingLinksStats('yourPage').contextual }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('structural')">Structural</span>
                <strong>{{ getIncomingLinksStats('yourPage').structural }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('same_page')">Same-page</span>
                <strong>{{ getIncomingLinksStats('yourPage').samePage }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('unknown')">Unknown</span>
                <strong>{{ getIncomingLinksStats('yourPage').unknown }}</strong>
              </div>
            </div>
            <div class="dropdown-section" *ngIf="hasAnalysis(yourPage)">
              <button
                class="dropdown-toggle"
                (click)="toggleIncomingLinks('yourPage')"
                type="button"
                [disabled]="incomingLinksLoading.yourPage"
              >
                <span *ngIf="!incomingLinksLoading.yourPage">
                  {{ isIncomingLinksExpanded('yourPage') ? 'Hide' : 'Show' }} Details
                </span>
                <span *ngIf="incomingLinksLoading.yourPage">Loading...</span>
                <span class="dropdown-arrow" [class.expanded]="isIncomingLinksExpanded('yourPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isIncomingLinksExpanded('yourPage')">
                <div class="link-context-note" *ngIf="incomingLinksError.yourPage">
                  ⚠️ Error: {{ incomingLinksError.yourPage }}
                </div>
                <div class="link-context-note" *ngIf="!incomingLinksError.yourPage && getIncomingLinksCount('yourPage') === 0">
                  No incoming links found from other pages on this domain.
                </div>
                <div class="link-list" *ngIf="!incomingLinksError.yourPage && getDisplayedIncomingLinks('yourPage').length > 0">
                  <div class="link-item" *ngFor="let link of getDisplayedIncomingLinks('yourPage')">
                    <div class="link-item-header">
                      <span class="link-type-pill" [ngClass]="getLinkTypeClass(link.linkType)" [title]="getLinkTypeTooltip(link.linkType)">
                        {{ getLinkTypeLabel(link.linkType) }}
                      </span>
                      <span class="link-meta" *ngIf="link.confidence != null">Confidence: {{ (link.confidence * 100) | number:'1.0-0' }}%</span>
                      <span class="link-meta" *ngIf="link.isBroken">Broken</span>
                      <span class="link-meta" *ngIf="link.dofollow === false">nofollow</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">From:</span>
                      <a class="link-item-value" [href]="link.sourceUrl" target="_blank" rel="noopener noreferrer">
                        {{ link.sourcePageTitle || link.sourceUrl }}
                      </a>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">To:</span>
                      <span class="link-item-value">{{ getLinkSourceUrl(yourPage) }}</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Anchor:</span>
                      <span class="link-item-value">"{{ formatAnchorText(link.anchorText) }}"</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Context:</span>
                      <span class="link-item-value">Not available (link sentence not stored).</span>
                    </div>
                  </div>
                </div>
                <button
                  class="link-show-more"
                  *ngIf="hasMoreIncomingLinks('yourPage') || showAllIncomingLinks.yourPage"
                  (click)="toggleShowAllIncomingLinks('yourPage')"
                >
                  {{ showAllIncomingLinks.yourPage ? 'Show less' : 'Show all links' }}
                </button>
                <div class="link-context-note" *ngIf="!incomingLinksError.yourPage && getDisplayedIncomingLinks('yourPage').length === 0 && getIncomingLinksCount('yourPage') > 0">
                  No contextual or same-page incoming links to show.
                </div>
              </div>
            </div>
          </div>

          <!-- Critical Issues with Dropdown -->
          <div class="metric-card">
            <div class="metric-label">Critical Issues</div>
            <div class="metric-value" [class.has-issues]="getCriticalIssuesCount(yourPage) > 0">
              {{ getCriticalIssuesCount(yourPage) }}
            </div>
            <div class="dropdown-section" *ngIf="getCriticalIssuesCount(yourPage) > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleCriticalIssues('yourPage')"
                type="button"
              >
                <span>{{ isCriticalIssuesExpanded('yourPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isCriticalIssuesExpanded('yourPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isCriticalIssuesExpanded('yourPage')">
                <div class="critical-issue-item" *ngFor="let issue of getCriticalIssues(yourPage)">
                  {{ issue }}
                </div>
              </div>
            </div>
          </div>

          <!-- Ranked Keywords with Dropdown -->
          <div class="metric-card">
            <div class="metric-label">Ranked Keywords</div>
            <div class="metric-value">{{ getRankedKeywordsCount(yourPage) }}</div>
            <div class="dropdown-section" *ngIf="getRankedKeywordsCount(yourPage) > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleRankedKeywords('yourPage')"
                type="button"
              >
                <span>{{ isRankedKeywordsExpanded('yourPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isRankedKeywordsExpanded('yourPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isRankedKeywordsExpanded('yourPage')">
                <div class="ranked-keywords-grid">
                  <div 
                    class="ranked-keyword-card" 
                    *ngFor="let keyword of getRankedKeywords(yourPage)"
                    [class.shared]="isKeywordShared('yourPage', keyword.keyword)"
                  >
                    <div class="ranked-keyword-label">{{ keyword.keyword }}</div>
                    <div class="ranked-keyword-meta">
                      <span class="ranked-keyword-position">Pos: {{ keyword.position }}</span>
                      <span class="ranked-keyword-volume" *ngIf="keyword.searchVolume">{{ keyword.searchVolume | number }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Related Pages with Dropdown (User Mode: Compact) -->
          <div class="metric-card" *ngIf="relatedPagesData && !loadingRelatedPages">
            <div class="metric-label">Related Pages</div>
            <div class="metric-value">{{ getRelatedPagesCount('yourPage') }}</div>
            <div class="dropdown-section" *ngIf="getRelatedPagesCount('yourPage') > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleRelatedPages()"
                type="button"
              >
                <span>{{ isRelatedPagesExpanded() ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isRelatedPagesExpanded()">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isRelatedPagesExpanded()">
                <div class="related-pages-compact" *ngIf="relatedPagesData.yourPage">
                  <div class="related-pages-grid-compact">
                    <div *ngFor="let page of relatedPagesData.yourPage.relatedPages" class="related-page-card-compact">
                      <div class="related-page-url-compact">
                        <a [href]="page.url" target="_blank" rel="noopener noreferrer">{{ page.url }}</a>
                      </div>
                      <div class="related-page-stats-compact">
                        <span class="stat-item-compact"><strong>Keywords:</strong> {{ page.keywordCount }}</span>
                        <span class="stat-item-compact"><strong>Search Volume:</strong> {{ page.totalSearchVolume | number }}</span>
                        <span class="stat-item-compact"><strong>Avg Position:</strong> {{ page.avgPosition }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Cost Breakdown -->
          <div class="metric-card cost-card" *ngIf="getCostBreakdown(yourPage) || totalEntityDiscoveryCost > 0">
            <div class="metric-label">API Cost</div>
            <div class="cost-total">{{ formatCost(getTotalCost(yourPage)) }}</div>
            <div class="cost-breakdown" *ngIf="getCostBreakdown(yourPage)">
              <div class="cost-item" *ngIf="getCostBreakdown(yourPage).onPageTask > 0">
                <span class="cost-label">OnPage Task:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(yourPage).onPageTask) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(yourPage).summary > 0">
                <span class="cost-label">Summary API:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(yourPage).summary) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(yourPage).pages > 0">
                <span class="cost-label">Pages API:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(yourPage).pages) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(yourPage).contentParsing > 0">
                <span class="cost-label">Content Parsing:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(yourPage).contentParsing) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(yourPage).rawHtml > 0">
                <span class="cost-label">Raw HTML:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(yourPage).rawHtml) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(yourPage).keywordDensity > 0">
                <span class="cost-label">Keyword Density:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(yourPage).keywordDensity) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(yourPage).rankedKeywords > 0">
                <span class="cost-label">Ranked Keywords:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(yourPage).rankedKeywords) }}</span>
              </div>
            </div>
            <div class="cost-breakdown" *ngIf="totalEntityDiscoveryCost > 0">
              <div class="cost-section-label" style="margin-top: 0.5rem; margin-bottom: 0.25rem; font-size: 0.75rem; color: #9ca3af; font-style: italic;">Shared Entity Discovery (split equally):</div>
              <div class="cost-item" *ngIf="serpCost > 0">
                <span class="cost-label">SERP Results:</span>
                <span class="cost-value">{{ formatCost(serpCost / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="(initialEntityCostBreakdown?.total ?? 0) > 0">
                <span class="cost-label">Initial Entities:</span>
                <span class="cost-value">{{ formatCost((initialEntityCostBreakdown?.total || 0) / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="(filteredEntityCostBreakdown?.total ?? 0) > 0">
                <span class="cost-label">Filtered Entities:</span>
                <span class="cost-value">{{ formatCost((filteredEntityCostBreakdown?.total || 0) / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="(sharedEntityCostBreakdown?.total ?? 0) > 0">
                <span class="cost-label">Shared Entities:</span>
                <span class="cost-value">{{ formatCost((sharedEntityCostBreakdown?.total || 0) / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="relatedPagesCostTotal > 0">
                <span class="cost-label">Related Pages:</span>
                <span class="cost-value">{{ formatCost(relatedPagesCostTotal / 2) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Competitor Page Column -->
      <div class="page-column">
        <h2>Competitor Page</h2>
        
        <!-- Status Section -->
        <div class="status-section">
          <div class="status-indicator" [class]="'status-' + competitorPage.status">
            <span class="status-dot"></span>
            <span class="status-text">{{ getStatusMessage(competitorPage) }}</span>
          </div>

          <!-- Data Source Badge -->
          <div 
            *ngIf="competitorPage.metadata.dataSource" 
            class="metadata-badge" 
            [class.from-database]="competitorPage.metadata.dataSource === 'database'" 
            [class.from-api]="competitorPage.metadata.dataSource === 'api'"
          >
            <span class="badge-icon">{{ competitorPage.metadata.dataSource === 'database' ? '💾' : '🌐' }}</span>
            <span class="badge-text">
              {{ competitorPage.metadata.dataSource === 'database' ? 'From Database' : 'From API' }}
            </span>
            <span *ngIf="competitorPage.metadata.lastUpdated" class="badge-timestamp">
              ({{ competitorPage.metadata.lastUpdated | date:'short' }})
            </span>
          </div>
        </div>

        <!-- Analysis Results -->
        <div class="analysis-results" *ngIf="hasAnalysis(competitorPage)">
          <div class="metric-card">
            <div class="metric-label">Page Title</div>
            <div class="metric-value">{{ getPageTitle(competitorPage) }}</div>
          </div>

          <div class="metric-card">
            <div class="metric-label">OnPage Score</div>
            <div class="metric-value score-value" [class]="'score-' + (getOnPageScore(competitorPage) || 0 >= 70 ? 'good' : getOnPageScore(competitorPage) || 0 >= 50 ? 'fair' : 'poor')">
              {{ getOnPageScore(competitorPage) || 'N/A' }}
            </div>
          </div>

          <div class="metric-card">
            <div class="metric-label">Word Count</div>
            <div class="metric-value">{{ getWordCount(competitorPage) || 'N/A' }}</div>
          </div>

          <!-- Freshness Card -->
          <div class="metric-card">
            <div class="metric-label">Freshness</div>
            <div class="freshness-content">
              <div class="freshness-row">
                <span class="freshness-label">Analyzed:</span>
                <span class="freshness-value">
                  <span *ngIf="getAnalyzedAt(competitorPage); else noAnalyzedAtCompetitor">
                    {{ getAnalyzedAt(competitorPage) | date:'short' }}
                  </span>
                  <ng-template #noAnalyzedAtCompetitor>N/A</ng-template>
                </span>
              </div>
              <div class="freshness-row" *ngIf="getLastModified(competitorPage)">
                <span class="freshness-label">Last modified:</span>
                <span class="freshness-value">{{ getLastModified(competitorPage) | date:'short' }}</span>
              </div>
            </div>
          </div>

          <!-- Topic Fit (Domain Topic Authority) -->
          <div class="metric-card topic-fit-card" *ngIf="getTopicSignalsCount(competitorPage) > 0">
            <div class="metric-label">
              Topic Fit
              <span class="info-tooltip" title="Shows competitor domain's authority on this page's main topic. Requires domain crawl and topic aggregation.">ⓘ</span>
            </div>
            <ng-container *ngIf="getTopicFitData('competitorPage') as topicFit">
              <!-- Loading state -->
              <div class="topic-fit-loading" *ngIf="topicFit.loading">
                <span class="loading-spinner-small"></span>
                Loading...
              </div>
              <!-- Error state -->
              <div class="topic-fit-error" *ngIf="topicFit.error">
                <span class="error-icon">⚠</span>
                Unable to load
              </div>
              <!-- No data state (domain not crawled/aggregated) -->
              <div class="topic-fit-unavailable" *ngIf="!topicFit.loading && !topicFit.error && !topicFit.hasData">
                <span class="muted-text">Domain topics not available yet</span>
                <span class="info-tooltip" title="Run a full domain crawl and topic aggregation to see this data.">ⓘ</span>
              </div>
              <!-- Topic not found in domain authority -->
              <div class="topic-fit-not-found" *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && !topicFit.topicFound">
                <span class="muted-text">Topic not in domain authority</span>
              </div>
              <!-- Topic found - show tier and score -->
              <div class="topic-fit-result" *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && topicFit.topicFound">
                <span class="tier-badge" [ngClass]="getTopicTierClass(topicFit.tier)">
                  {{ formatTopicTier(topicFit.tier) }}
                </span>
                <span class="topic-fit-details">
                  on <strong>{{ topicFit.mainTopicLabel }}</strong>
                  <span class="authority-score">(Authority {{ topicFit.authorityScore | number:'1.0-0' }})</span>
                </span>
              </div>
            </ng-container>
          </div>

          <!-- Topic Signals with Dropdown -->
          <div class="metric-card">
            <div class="metric-label">Topic Signals</div>
            <div class="metric-value">{{ getTopicSignalsCount(competitorPage) }}</div>
            <div class="dropdown-section" *ngIf="getTopicSignalsCount(competitorPage) > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleTopicSignals('competitorPage')"
                type="button"
              >
                <span>{{ isTopicSignalsExpanded('competitorPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isTopicSignalsExpanded('competitorPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isTopicSignalsExpanded('competitorPage')">
                <div class="topic-signals-grid">
                  <div 
                    class="topic-signal-card" 
                    [class.shared]="isTopicSignalShared(signal, 'competitorPage')"
                    *ngFor="let signal of getTopicSignals(competitorPage)"
                  >
                    <div class="topic-signal-label">{{ signal.label }}</div>
                    <div class="topic-signal-meta">
                      <span class="topic-signal-weight">{{ formatWeight(signal.weight) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Internal Links -->
          <div class="metric-card">
            <div class="metric-label">Internal Links</div>
            <div class="metric-value">{{ getLinksCount(competitorPage) }}</div>
            <div class="link-warning" *ngIf="shouldShowLinkWarning(competitorPageDomain, competitorPage)">
              ⚠️ You can only classify links after a full domain crawl.
            </div>
            <div class="link-breakdown" *ngIf="shouldShowLinkBreakdown(competitorPageDomain, competitorPage)">
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('contextual')">Contextual</span>
                <strong>{{ getLinkStats(competitorPage).contextual }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('structural')">Structural</span>
                <strong>{{ getLinkStats(competitorPage).structural }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('same_page')">Same-page</span>
                <strong>{{ getLinkStats(competitorPage).samePage }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('unknown')">Unknown</span>
                <strong>{{ getLinkStats(competitorPage).unknown }}</strong>
              </div>
              <div class="link-note" *ngIf="getLinkClassificationStatus(competitorPageDomain) === 'partial'">
                Partial classification for this domain.
              </div>
            </div>
            <div class="dropdown-section" *ngIf="getLinksCount(competitorPage) > 0">
              <button
                class="dropdown-toggle"
                (click)="toggleInternalLinks('competitorPage')"
                type="button"
              >
                <span>{{ isInternalLinksExpanded('competitorPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isInternalLinksExpanded('competitorPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isInternalLinksExpanded('competitorPage')">
                <div class="link-context-note">
                  Full sentence context is not captured in the current link data.
                </div>
                <div class="link-list">
                  <div class="link-item" *ngFor="let link of getDisplayedInternalLinks(competitorPage, 'competitorPage')">
                    <div class="link-item-header">
                      <span class="link-type-pill" [ngClass]="getLinkTypeClass(link.linkType)" [title]="getLinkTypeTooltip(link.linkType)">
                        {{ getLinkTypeLabel(link.linkType) }}
                      </span>
                      <span class="link-meta" *ngIf="link.confidence != null">Confidence: {{ (link.confidence * 100) | number:'1.0-0' }}%</span>
                      <span class="link-meta" *ngIf="link.isBroken">Broken</span>
                      <span class="link-meta" *ngIf="link.dofollow === false">nofollow</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">From:</span>
                      <span class="link-item-value">{{ getLinkSourceUrl(competitorPage) }}</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">To:</span>
                      <a class="link-item-value" [href]="link.targetUrl" target="_blank" rel="noopener noreferrer">
                        {{ link.targetUrl || link.targetRelativeUrl }}
                      </a>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Anchor:</span>
                      <span class="link-item-value">“{{ formatAnchorText(link.anchorText) }}”</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Context:</span>
                      <span class="link-item-value">Not available (link sentence not stored).</span>
                    </div>
                  </div>
                </div>
                <button
                  class="link-show-more"
                  *ngIf="hasMoreInternalLinks(competitorPage, 'competitorPage') || showAllInternalLinks.competitorPage"
                  (click)="toggleShowAllInternalLinks('competitorPage')"
                >
                  {{ showAllInternalLinks.competitorPage ? 'Show less' : 'Show all links' }}
                </button>
                <div class="link-context-note" *ngIf="getFilteredInternalLinks(competitorPage).length === 0">
                  No contextual or same-page links to show.
                </div>
              </div>
            </div>
          </div>

          <!-- Incoming Links -->
          <div class="metric-card" *ngIf="hasAnalysis(competitorPage)">
            <div class="metric-label">Incoming Links</div>
            <div class="metric-value">{{ getIncomingLinksCount('competitorPage') }}</div>
            <div class="link-warning" *ngIf="shouldShowIncomingLinksWarning('competitorPage')">
              ⚠️ Full domain crawl recommended for complete incoming links data.
            </div>
            <div class="link-breakdown" *ngIf="getIncomingLinksCount('competitorPage') > 0">
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('contextual')">Contextual</span>
                <strong>{{ getIncomingLinksStats('competitorPage').contextual }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('structural')">Structural</span>
                <strong>{{ getIncomingLinksStats('competitorPage').structural }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('same_page')">Same-page</span>
                <strong>{{ getIncomingLinksStats('competitorPage').samePage }}</strong>
              </div>
              <div class="link-stat">
                <span class="link-type-label" [title]="getLinkTypeTooltip('unknown')">Unknown</span>
                <strong>{{ getIncomingLinksStats('competitorPage').unknown }}</strong>
              </div>
            </div>
            <div class="dropdown-section" *ngIf="hasAnalysis(competitorPage)">
              <button
                class="dropdown-toggle"
                (click)="toggleIncomingLinks('competitorPage')"
                type="button"
                [disabled]="incomingLinksLoading.competitorPage"
              >
                <span *ngIf="!incomingLinksLoading.competitorPage">
                  {{ isIncomingLinksExpanded('competitorPage') ? 'Hide' : 'Show' }} Details
                </span>
                <span *ngIf="incomingLinksLoading.competitorPage">Loading...</span>
                <span class="dropdown-arrow" [class.expanded]="isIncomingLinksExpanded('competitorPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isIncomingLinksExpanded('competitorPage')">
                <div class="link-context-note" *ngIf="incomingLinksError.competitorPage">
                  ⚠️ Error: {{ incomingLinksError.competitorPage }}
                </div>
                <div class="link-context-note" *ngIf="!incomingLinksError.competitorPage && getIncomingLinksCount('competitorPage') === 0">
                  No incoming links found from other pages on this domain.
                </div>
                <div class="link-list" *ngIf="!incomingLinksError.competitorPage && getDisplayedIncomingLinks('competitorPage').length > 0">
                  <div class="link-item" *ngFor="let link of getDisplayedIncomingLinks('competitorPage')">
                    <div class="link-item-header">
                      <span class="link-type-pill" [ngClass]="getLinkTypeClass(link.linkType)" [title]="getLinkTypeTooltip(link.linkType)">
                        {{ getLinkTypeLabel(link.linkType) }}
                      </span>
                      <span class="link-meta" *ngIf="link.confidence != null">Confidence: {{ (link.confidence * 100) | number:'1.0-0' }}%</span>
                      <span class="link-meta" *ngIf="link.isBroken">Broken</span>
                      <span class="link-meta" *ngIf="link.dofollow === false">nofollow</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">From:</span>
                      <a class="link-item-value" [href]="link.sourceUrl" target="_blank" rel="noopener noreferrer">
                        {{ link.sourcePageTitle || link.sourceUrl }}
                      </a>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">To:</span>
                      <span class="link-item-value">{{ getLinkSourceUrl(competitorPage) }}</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Anchor:</span>
                      <span class="link-item-value">"{{ formatAnchorText(link.anchorText) }}"</span>
                    </div>
                    <div class="link-item-row">
                      <span class="link-item-label">Context:</span>
                      <span class="link-item-value">Not available (link sentence not stored).</span>
                    </div>
                  </div>
                </div>
                <button
                  class="link-show-more"
                  *ngIf="hasMoreIncomingLinks('competitorPage') || showAllIncomingLinks.competitorPage"
                  (click)="toggleShowAllIncomingLinks('competitorPage')"
                >
                  {{ showAllIncomingLinks.competitorPage ? 'Show less' : 'Show all links' }}
                </button>
                <div class="link-context-note" *ngIf="!incomingLinksError.competitorPage && getDisplayedIncomingLinks('competitorPage').length === 0 && getIncomingLinksCount('competitorPage') > 0">
                  No contextual or same-page incoming links to show.
                </div>
              </div>
            </div>
          </div>

          <!-- Critical Issues with Dropdown -->
          <div class="metric-card">
            <div class="metric-label">Critical Issues</div>
            <div class="metric-value" [class.has-issues]="getCriticalIssuesCount(competitorPage) > 0">
              {{ getCriticalIssuesCount(competitorPage) }}
            </div>
            <div class="dropdown-section" *ngIf="getCriticalIssuesCount(competitorPage) > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleCriticalIssues('competitorPage')"
                type="button"
              >
                <span>{{ isCriticalIssuesExpanded('competitorPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isCriticalIssuesExpanded('competitorPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isCriticalIssuesExpanded('competitorPage')">
                <div class="critical-issue-item" *ngFor="let issue of getCriticalIssues(competitorPage)">
                  {{ issue }}
                </div>
              </div>
            </div>
          </div>

          <!-- Ranked Keywords with Dropdown -->
          <div class="metric-card">
            <div class="metric-label">Ranked Keywords</div>
            <div class="metric-value">{{ getRankedKeywordsCount(competitorPage) }}</div>
            <div class="dropdown-section" *ngIf="getRankedKeywordsCount(competitorPage) > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleRankedKeywords('competitorPage')"
                type="button"
              >
                <span>{{ isRankedKeywordsExpanded('competitorPage') ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isRankedKeywordsExpanded('competitorPage')">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isRankedKeywordsExpanded('competitorPage')">
                <div class="ranked-keywords-grid">
                  <div 
                    class="ranked-keyword-card" 
                    *ngFor="let keyword of getRankedKeywords(competitorPage)"
                    [class.shared]="isKeywordShared('competitorPage', keyword.keyword)"
                  >
                    <div class="ranked-keyword-label">{{ keyword.keyword }}</div>
                    <div class="ranked-keyword-meta">
                      <span class="ranked-keyword-position">Pos: {{ keyword.position }}</span>
                      <span class="ranked-keyword-volume" *ngIf="keyword.searchVolume">{{ keyword.searchVolume | number }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Related Pages with Dropdown (User Mode: Compact) -->
          <div class="metric-card" *ngIf="relatedPagesData && !loadingRelatedPages">
            <div class="metric-label">Related Pages</div>
            <div class="metric-value">{{ getRelatedPagesCount('competitorPage') }}</div>
            <div class="dropdown-section" *ngIf="getRelatedPagesCount('competitorPage') > 0">
              <button 
                class="dropdown-toggle" 
                (click)="toggleRelatedPages()"
                type="button"
              >
                <span>{{ isRelatedPagesExpanded() ? 'Hide' : 'Show' }} Details</span>
                <span class="dropdown-arrow" [class.expanded]="isRelatedPagesExpanded()">▼</span>
              </button>
              <div class="dropdown-content" *ngIf="isRelatedPagesExpanded()">
                <div class="related-pages-compact" *ngIf="relatedPagesData.competitorPage">
                  <div class="related-pages-grid-compact">
                    <div *ngFor="let page of relatedPagesData.competitorPage.relatedPages" class="related-page-card-compact">
                      <div class="related-page-url-compact">
                        <a [href]="page.url" target="_blank" rel="noopener noreferrer">{{ page.url }}</a>
                      </div>
                      <div class="related-page-stats-compact">
                        <span class="stat-item-compact"><strong>Keywords:</strong> {{ page.keywordCount }}</span>
                        <span class="stat-item-compact"><strong>Search Volume:</strong> {{ page.totalSearchVolume | number }}</span>
                        <span class="stat-item-compact"><strong>Avg Position:</strong> {{ page.avgPosition }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Cost Breakdown -->
          <div class="metric-card cost-card" *ngIf="getCostBreakdown(competitorPage) || totalEntityDiscoveryCost > 0">
            <div class="metric-label">API Cost</div>
            <div class="cost-total">{{ formatCost(getTotalCost(competitorPage)) }}</div>
            <div class="cost-breakdown" *ngIf="getCostBreakdown(competitorPage)">
              <div class="cost-item" *ngIf="getCostBreakdown(competitorPage).onPageTask > 0">
                <span class="cost-label">OnPage Task:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(competitorPage).onPageTask) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(competitorPage).summary > 0">
                <span class="cost-label">Summary API:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(competitorPage).summary) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(competitorPage).pages > 0">
                <span class="cost-label">Pages API:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(competitorPage).pages) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(competitorPage).contentParsing > 0">
                <span class="cost-label">Content Parsing:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(competitorPage).contentParsing) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(competitorPage).rawHtml > 0">
                <span class="cost-label">Raw HTML:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(competitorPage).rawHtml) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(competitorPage).keywordDensity > 0">
                <span class="cost-label">Keyword Density:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(competitorPage).keywordDensity) }}</span>
              </div>
              <div class="cost-item" *ngIf="getCostBreakdown(competitorPage).rankedKeywords > 0">
                <span class="cost-label">Ranked Keywords:</span>
                <span class="cost-value">{{ formatCost(getCostBreakdown(competitorPage).rankedKeywords) }}</span>
              </div>
            </div>
            <div class="cost-breakdown" *ngIf="totalEntityDiscoveryCost > 0">
              <div class="cost-section-label" style="margin-top: 0.5rem; margin-bottom: 0.25rem; font-size: 0.75rem; color: #9ca3af; font-style: italic;">Shared Entity Discovery (split equally):</div>
              <div class="cost-item" *ngIf="serpCost > 0">
                <span class="cost-label">SERP Results:</span>
                <span class="cost-value">{{ formatCost(serpCost / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="(initialEntityCostBreakdown?.total ?? 0) > 0">
                <span class="cost-label">Initial Entities:</span>
                <span class="cost-value">{{ formatCost((initialEntityCostBreakdown?.total || 0) / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="(filteredEntityCostBreakdown?.total ?? 0) > 0">
                <span class="cost-label">Filtered Entities:</span>
                <span class="cost-value">{{ formatCost((filteredEntityCostBreakdown?.total || 0) / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="(sharedEntityCostBreakdown?.total ?? 0) > 0">
                <span class="cost-label">Shared Entities:</span>
                <span class="cost-value">{{ formatCost((sharedEntityCostBreakdown?.total || 0) / 2) }}</span>
              </div>
              <div class="cost-item" *ngIf="relatedPagesCostTotal > 0">
                <span class="cost-label">Related Pages:</span>
                <span class="cost-value">{{ formatCost(relatedPagesCostTotal / 2) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Topic Share Coverage Card (Mini Hook) -->
    <!-- Shows how many shared topic entities each domain covers -->
    <div class="topic-share-coverage-section" *ngIf="shouldShowTopicShareCard()">
      <ng-container *ngIf="getTopicShareCoverageData() as coverage">
        <div class="topic-share-coverage-card">
          <div class="coverage-header">
            <h3>
              <span class="header-icon">📊</span>
              Shared Entity Coverage
              <span class="info-tooltip" title="Compares how many shared topic entities each domain covers based on Topic Share 3 analysis. Requires running Topic Share for this topic.">ⓘ</span>
            </h3>
            <span class="coverage-topic" *ngIf="coverage.topic">
              Topic: <strong>{{ coverage.topic }}</strong>
            </span>
          </div>
          
          <!-- Note when pages have different main topics -->
          <div class="topic-difference-note" *ngIf="getTopicDifferenceInfo() as diff">
            <span class="note-icon">ℹ️</span>
            <span class="note-text">
              Pages have different main topics: your page is about "<strong>{{ diff.yourTopic }}</strong>", 
              competitor's is about "<strong>{{ diff.competitorTopic }}</strong>". 
              Showing coverage for your page's topic.
            </span>
          </div>

          <!-- Loading state -->
          <div class="coverage-loading" *ngIf="coverage.loading">
            <span class="loading-spinner-small"></span>
            <span>Loading Topic Share coverage...</span>
          </div>

          <!-- Error state -->
          <div class="coverage-error" *ngIf="coverage.error">
            <span class="error-icon">⚠</span>
            <span>{{ coverage.error }}</span>
          </div>

          <!-- Not available state -->
          <div class="coverage-unavailable" *ngIf="!coverage.loading && !coverage.error && !coverage.available">
            <div class="unavailable-message">
              <span class="unavailable-icon">📋</span>
              <span class="unavailable-text">{{ coverage.reason || 'Topic Share analysis not yet available for this topic.' }}</span>
            </div>
            <p class="unavailable-hint">
              Run Topic Share 3 analysis for <strong>{{ coverage.topic || 'this topic' }}</strong> to see shared entity coverage.
            </p>
            <a [href]="getTopicShareDeepLink()" class="topic-share-deep-link">
              <span class="link-icon">📊</span>
              Run Topic Share 3 for "{{ coverage.topic || 'this topic' }}"
              <span class="link-arrow">→</span>
            </a>
            <p class="deep-link-note">
              This will open Topic Share 3 with your topic and both domains pre-filled.
            </p>
          </div>

          <!-- Available state - show coverage comparison -->
          <div class="coverage-content" *ngIf="!coverage.loading && !coverage.error && coverage.available">
            <div class="coverage-comparison">
              <!-- Your Domain -->
              <div class="coverage-domain your-domain">
                <div class="domain-header">
                  <span class="domain-label">You</span>
                  <span class="domain-name" *ngIf="coverage.yourDomain">{{ coverage.yourDomain.domain }}</span>
                </div>
                <div class="coverage-percentage" *ngIf="coverage.yourDomain">
                  <span class="percentage-value">{{ coverage.yourDomain.coveragePercentage | number:'1.0-0' }}%</span>
                  <span class="percentage-detail">{{ coverage.yourDomain.entitiesCovered }} of {{ coverage.yourDomain.totalEntities }} entities</span>
                </div>
                <div class="coverage-bar">
                  <div class="coverage-fill your-fill" [style.width.%]="coverage.yourDomain?.coveragePercentage || 0"></div>
                </div>
              </div>

              <!-- Competitor Domain -->
              <div class="coverage-domain competitor-domain">
                <div class="domain-header">
                  <span class="domain-label">Competitor</span>
                  <span class="domain-name" *ngIf="coverage.competitorDomain">{{ coverage.competitorDomain.domain }}</span>
                </div>
                <div class="coverage-percentage" *ngIf="coverage.competitorDomain">
                  <span class="percentage-value">{{ coverage.competitorDomain.coveragePercentage | number:'1.0-0' }}%</span>
                  <span class="percentage-detail">{{ coverage.competitorDomain.entitiesCovered }} of {{ coverage.competitorDomain.totalEntities }} entities</span>
                </div>
                <div class="coverage-bar">
                  <div class="coverage-fill competitor-fill" [style.width.%]="coverage.competitorDomain?.coveragePercentage || 0"></div>
                </div>
              </div>
            </div>

            <!-- Top URLs toggle -->
            <div class="coverage-urls-section" *ngIf="(coverage.yourDomain?.topUrls?.length || 0) > 0 || (coverage.competitorDomain?.topUrls?.length || 0) > 0">
              <button class="urls-toggle-btn" (click)="toggleTopicShareUrls()">
                {{ topicShareUrlsExpanded ? '▼' : '▶' }} Top Ranking URLs
                <span class="urls-count">({{ (coverage.yourDomain?.topUrls?.length || 0) + (coverage.competitorDomain?.topUrls?.length || 0) }})</span>
              </button>

              <div class="urls-content" *ngIf="topicShareUrlsExpanded">
                <!-- Your URLs -->
                <div class="urls-list your-urls" *ngIf="coverage.yourDomain?.topUrls?.length">
                  <h4>Your Top URLs</h4>
                  <ul>
                    <li *ngFor="let url of coverage.yourDomain?.topUrls || []">
                      <a [href]="url.url" target="_blank" rel="noopener" class="url-link">{{ url.title || url.url }}</a>
                      <span class="url-entities">({{ url.entitiesCount }} entities)</span>
                    </li>
                  </ul>
                </div>

                <!-- Competitor URLs -->
                <div class="urls-list competitor-urls" *ngIf="coverage.competitorDomain?.topUrls?.length">
                  <h4>Competitor Top URLs</h4>
                  <ul>
                    <li *ngFor="let url of coverage.competitorDomain?.topUrls || []">
                      <a [href]="url.url" target="_blank" rel="noopener" class="url-link">{{ url.title || url.url }}</a>
                      <span class="url-entities">({{ url.entitiesCount }} entities)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Last updated -->
            <div class="coverage-meta" *ngIf="coverage.lastUpdated">
              <span class="meta-text">Topic Share data from {{ coverage.lastUpdated | date:'medium' }}</span>
            </div>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Diagnosis Banner ("Why They Probably Win") -->
    <!-- This is a LIVING FEATURE that evolves as new data becomes available -->
    <div class="diagnosis-banner" 
         *ngIf="hasAnalysis(yourPage) && hasAnalysis(competitorPage) && getDiagnosisBullets().length > 0"
         [class.competitor-advantage]="hasCompetitorAdvantages() && !hasYourAdvantages()"
         [class.your-advantage]="hasYourAdvantages() && !hasCompetitorAdvantages()"
         [class.mixed-results]="hasCompetitorAdvantages() && hasYourAdvantages()">
      <div class="diagnosis-header">
        <span class="diagnosis-icon" *ngIf="hasCompetitorAdvantages() && !hasYourAdvantages()">⚠️</span>
        <span class="diagnosis-icon" *ngIf="hasYourAdvantages() && !hasCompetitorAdvantages()">✅</span>
        <span class="diagnosis-icon" *ngIf="hasCompetitorAdvantages() && hasYourAdvantages()">📊</span>
        <h3 *ngIf="hasCompetitorAdvantages() && !hasYourAdvantages()">Why They Probably Win</h3>
        <h3 *ngIf="hasYourAdvantages() && !hasCompetitorAdvantages()">Your Competitive Advantages</h3>
        <h3 *ngIf="hasCompetitorAdvantages() && hasYourAdvantages()">Competitive Analysis</h3>
      </div>
      <ul class="diagnosis-list">
        <li *ngFor="let bullet of getDiagnosisBullets()" 
            [class.competitor-wins]="bullet.winner === 'competitor'"
            [class.you-win]="bullet.winner === 'you'"
            [class.category-keywords]="bullet.category === 'keywords'"
            [class.category-links]="bullet.category === 'links'"
            [class.category-freshness]="bullet.category === 'freshness'"
            [class.category-content]="bullet.category === 'content'"
            [class.category-topicAuthority]="bullet.category === 'topicAuthority'">
          <span class="bullet-icon">
            <span *ngIf="bullet.winner === 'competitor'">−</span>
            <span *ngIf="bullet.winner === 'you'">+</span>
          </span>
          <span class="bullet-text">{{ bullet.text }}</span>
        </li>
      </ul>
      <p class="diagnosis-note">
        Based on keyword positions, link structure, content freshness, and domain topic authority.
        <span class="diagnosis-hint">This analysis evolves as more data becomes available.</span>
      </p>
    </div>

    <!-- Keyword Overlap & Opportunities Section -->
    <div class="keyword-overlap-section" *ngIf="computeKeywordOverlap() as overlap">
      <h3 class="section-title">Keyword Overlap & Opportunities</h3>
      
      <div class="overlap-summary">
        <div class="overlap-stat shared">
          <div class="stat-label">Shared Keywords</div>
          <div class="stat-value">{{ overlap.sharedCount }}</div>
          <div class="stat-detail" *ngIf="overlap.sharedCount > 0">
            Your avg pos: <strong>{{ overlap.sharedAvgPositionYour }}</strong> · 
            Competitor avg pos: <strong>{{ overlap.sharedAvgPositionCompetitor }}</strong>
          </div>
        </div>
        
        <div class="overlap-stat your-unique">
          <div class="stat-label">Your Unique Keywords</div>
          <div class="stat-value">{{ overlap.yourUniqueCount }}</div>
        </div>
        
        <div class="overlap-stat competitor-unique">
          <div class="stat-label">Competitor Unique Keywords</div>
          <div class="stat-value">{{ overlap.competitorUniqueCount }}</div>
        </div>
      </div>

      <!-- Content Opportunities -->
      <div class="opportunities-section" *ngIf="getContentOpportunities().length > 0">
        <button 
          class="opportunities-toggle" 
          (click)="toggleKeywordOpportunities()"
          type="button"
        >
          <span class="opportunities-icon">💡</span>
          <span>{{ getContentOpportunities().length }} Content Opportunities</span>
          <span class="dropdown-arrow" [class.expanded]="keywordOpportunitiesExpanded">▼</span>
        </button>
        
        <div class="opportunities-content" *ngIf="keywordOpportunitiesExpanded">
          <p class="opportunities-hint">These competitor-only keywords have good search volume and rankings. Consider covering these topics:</p>
          <div class="opportunities-list">
            <div class="opportunity-item" *ngFor="let opp of getContentOpportunities()">
              <span class="opportunity-keyword">{{ opp.keyword }}</span>
              <div class="opportunity-meta">
                <span class="opportunity-position">Pos: {{ opp.position }}</span>
                <span class="opportunity-volume">{{ opp.searchVolume | number }} vol</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>



  <!-- Crawl Estimate + Processing Status -->
  <div class="crawl-management" *ngIf="showCrawlPrompt && featureFlags.isEnabled('crawlManagement')">
    <h3>Full Domain Crawl</h3>
    <p>Want deeper domain-level analysis? Estimate a full crawl to see topic authority, link classification, and more.</p>

    <div class="domain-status-grid">
      <div class="domain-status-card" *ngFor="let domain of crawlDomains">
        <div class="domain-status-header">
          <h4>{{ domain }}</h4>
          <span class="status-pill" [ngClass]="getCrawlStatusClass(getDomainCrawlState(domain).status)">
            {{ getCrawlStatusText(getDomainCrawlState(domain).status) }}
          </span>
        </div>

        <div class="crawl-status-block">
          <!-- Existing crawl in progress or complete -->
          <ng-container *ngIf="getDomainCrawlState(domain).status as status">
            <ng-container *ngIf="status.phase === 'crawling' || status.phase === 'analyzing' || status.pagesInQueue > 0 || status.totalUrls > 10 || crawlConfirmed.get(domain)">
              <div class="status-summary">
                <ng-container *ngIf="status.phase === 'analyzing'">
                  <span class="status-item">Pages Crawled: <strong>{{ status.pagesCrawled }}</strong></span>
                  <span class="status-item">Analyzing: <strong>{{ status.pagesAnalyzed || 0 }} / {{ status.totalUrls }}</strong></span>
                </ng-container>
                <ng-container *ngIf="status.phase === 'complete'">
                  <span class="status-item">Crawl Complete: <strong>{{ status.pagesAnalyzed || status.pagesCrawled }} / {{ status.totalUrls }} pages</strong></span>
                </ng-container>
                <ng-container *ngIf="status.phase !== 'analyzing' && status.phase !== 'complete'">
                  <span class="status-item">Pages Crawled: <strong>{{ status.pagesCrawled }} / {{ status.totalUrls }}</strong></span>
                  <span class="status-item">In Queue: <strong>{{ status.pagesInQueue }}</strong></span>
                </ng-container>
              </div>
              <div class="progress-bar-container">
                <div class="progress-bar">
                  <div class="progress-fill" [style.width.%]="status.progress"></div>
                </div>
                <span class="progress-percentage">{{ status.progress }}%</span>
              </div>
            </ng-container>
          </ng-container>

          <!-- No existing crawl: show estimate flow -->
          <ng-container *ngIf="!getDomainCrawlState(domain).status || (getDomainCrawlState(domain).status!.phase !== 'crawling' && getDomainCrawlState(domain).status!.phase !== 'analyzing' && getDomainCrawlState(domain).status!.totalUrls <= 10 && getDomainCrawlState(domain).status!.pagesInQueue === 0 && !crawlConfirmed.get(domain))">
            <!-- Loading crawl status check -->
            <div class="loading" *ngIf="getDomainCrawlState(domain).loading">
              <p>Checking crawl status...</p>
            </div>

            <!-- Crawl status error -->
            <div class="error-message" *ngIf="getDomainCrawlState(domain).error">
              <p>{{ getDomainCrawlState(domain).error }}</p>
            </div>

            <!-- Estimate not yet requested -->
            <div class="crawl-estimate-actions" *ngIf="!crawlEstimates.get(domain) && !crawlEstimateLoading.get(domain) && !getDomainCrawlState(domain).loading">
              <p class="muted">No crawl found for this domain.</p>
              <button
                (click)="estimateDomainCrawl(domain)"
                class="crawl-button estimate-button"
              >
                Estimate Crawl
              </button>
            </div>

            <!-- Estimate loading -->
            <div class="crawl-estimate-loading" *ngIf="crawlEstimateLoading.get(domain)">
              <span class="loading-spinner-small"></span>
              <span>Parsing sitemap...</span>
            </div>

            <!-- Estimate error -->
            <div class="error-message" *ngIf="crawlEstimateError.get(domain)">
              <p>{{ crawlEstimateError.get(domain) }}</p>
              <button (click)="estimateDomainCrawl(domain)" class="crawl-button estimate-button">Retry</button>
            </div>

            <!-- Estimate result: confirm to start -->
            <div class="crawl-estimate-result" *ngIf="crawlEstimates.get(domain) as estimate">
              <div class="estimate-details">
                <div class="estimate-row">
                  <span class="estimate-label">Sitemap pages:</span>
                  <strong>{{ estimate.totalPagesInSitemap }}</strong>
                </div>
                <div class="estimate-row">
                  <span class="estimate-label">Pages to crawl:</span>
                  <strong>{{ estimate.filteredPages }}</strong>
                  <span class="estimate-note" *ngIf="estimate.totalPagesInSitemap > estimate.limit">(capped at {{ estimate.limit }})</span>
                </div>
                <div class="estimate-row estimate-cost-row">
                  <span class="estimate-label">Estimated cost:</span>
                  <strong class="estimate-cost">${{ estimate.estimatedTotalCost | number:'1.2-2' }}</strong>
                  <span class="estimate-note">(~${{ estimate.estimatedCostPerPage | number:'1.4-4' }}/page)</span>
                </div>
              </div>
              <label class="cwv-toggle">
                <input type="checkbox"
                  [checked]="enableCoreWebVitals.get(domain)"
                  (change)="enableCoreWebVitals.set(domain, $any($event.target).checked)">
                <span class="cwv-toggle-label">Include Core Web Vitals</span>
                <span class="cwv-toggle-note">(slower, adds LCP/CLS/INP metrics)</span>
              </label>
              <button
                (click)="confirmAndStartCrawl(domain)"
                [disabled]="getDomainCrawlState(domain).starting || crawlConfirmed.get(domain)"
                class="crawl-button confirm-crawl-button"
              >
                {{ getDomainCrawlState(domain).starting ? 'Starting...' : 'Start Crawl' }}
              </button>
            </div>
          </ng-container>
        </div>

        <div class="processing-status-block">
          <div class="processing-header">
            <h5>Processing Status</h5>
            <button
              *ngIf="getDomainProcessingState(domain).status && !isAllProcessingComplete(getDomainProcessingState(domain).status!)"
              class="btn-run-all"
              [disabled]="processingRunning.get(domain)"
              (click)="runAllProcessing(domain)">
              {{ processingRunning.get(domain) ? 'Running...' : 'Run All Processing' }}
            </button>
          </div>
          <div class="loading" *ngIf="getDomainProcessingState(domain).loading && !getDomainProcessingState(domain).status">
            <p>Loading processing status...</p>
          </div>
          <div class="error-message" *ngIf="getDomainProcessingState(domain).error">
            <p>{{ getDomainProcessingState(domain).error }}</p>
          </div>
          <ng-container *ngIf="getDomainProcessingState(domain).status as processing">
            <div class="processing-row">
              <span class="processing-label">links:backfill</span>
              <span class="status-pill" [ngClass]="getProcessingStatusClass(processing.linksBackfill.status)">
                {{ formatProcessingStatus(processing.linksBackfill.status) }}
              </span>
              <span class="processing-meta">{{ processing.linksBackfill.pagesWithLinks }} / {{ processing.linksBackfill.totalPages }} pages</span>
              <span class="processing-meta" *ngIf="processing.linksBackfill.lastFetchedAt">Updated {{ formatDate(processing.linksBackfill.lastFetchedAt) }}</span>
              <button
                *ngIf="processing.linksBackfill.status !== 'complete' && !processingRunning.get(domain)"
                class="btn-run-step"
                [disabled]="stepRunning.get(domain + ':links-backfill')"
                (click)="runProcessingStep(domain, 'links-backfill')">
                {{ stepRunning.get(domain + ':links-backfill') ? 'Running...' : 'Run' }}
              </button>
            </div>
            <div class="processing-row">
              <span class="processing-label">links:classify</span>
              <span class="status-pill" [ngClass]="getProcessingStatusClass(processing.linksClassify.status)">
                {{ formatProcessingStatus(processing.linksClassify.status) }}
              </span>
              <span class="processing-meta">{{ processing.linksClassify.pagesWithClassifiedLinks }} / {{ processing.linksClassify.pagesWithLinks }} pages</span>
              <span class="processing-meta" *ngIf="processing.linksClassify.lastClassifiedAt">Updated {{ formatDate(processing.linksClassify.lastClassifiedAt) }}</span>
              <button
                *ngIf="processing.linksClassify.status !== 'complete' && !processingRunning.get(domain)"
                class="btn-run-step"
                [disabled]="stepRunning.get(domain + ':links-classify') || processing.linksBackfill.status === 'not_started'"
                (click)="runProcessingStep(domain, 'links-classify')">
                {{ stepRunning.get(domain + ':links-classify') ? 'Running...' : 'Run' }}
              </button>
            </div>
            <div class="processing-row">
              <span class="processing-label">topics:map</span>
              <span class="status-pill" [ngClass]="getProcessingStatusClass(processing.topicsMap.status)">
                {{ formatProcessingStatus(processing.topicsMap.status) }}
              </span>
              <span class="processing-meta">{{ processing.topicsMap.mappingCount }} mappings</span>
              <span class="processing-meta" *ngIf="processing.topicsMap.lastUpdatedAt">Updated {{ formatDate(processing.topicsMap.lastUpdatedAt) }}</span>
              <button
                *ngIf="processing.topicsMap.status !== 'complete' && !processingRunning.get(domain)"
                class="btn-run-step"
                [disabled]="stepRunning.get(domain + ':topics-map')"
                (click)="runProcessingStep(domain, 'topics-map')">
                {{ stepRunning.get(domain + ':topics-map') ? 'Running...' : 'Run' }}
              </button>
            </div>
            <div class="processing-row">
              <span class="processing-label">topics:aggregate</span>
              <span class="status-pill" [ngClass]="getProcessingStatusClass(processing.topicsAggregate.status)">
                {{ formatProcessingStatus(processing.topicsAggregate.status) }}
              </span>
              <span class="processing-meta">{{ processing.topicsAggregate.aggregateCount }} aggregates</span>
              <span class="processing-meta" *ngIf="processing.topicsAggregate.lastUpdatedAt">Updated {{ formatDate(processing.topicsAggregate.lastUpdatedAt) }}</span>
              <button
                *ngIf="processing.topicsAggregate.status !== 'complete' && !processingRunning.get(domain)"
                class="btn-run-step"
                [disabled]="stepRunning.get(domain + ':topics-aggregate') || processing.topicsMap.status === 'not_started'"
                (click)="runProcessingStep(domain, 'topics-aggregate')">
                {{ stepRunning.get(domain + ':topics-aggregate') ? 'Running...' : 'Run' }}
              </button>
            </div>
          </ng-container>
        </div>
      </div>
    </div>
  </div>

</div>

